// ============================================================================
// THE PHONE'S NAVIGATION RULES — docs/design/handoff-mobile-v10, boards 03/04 (tabs) and 25
// (screen transitions). Pure, so every rule below is pinned by mobile/shell/shell.test.ts.
//
// FIVE TABS (owner, 2026-10-18): Day · School · Chat · Reminders · Notes, + on the right, and the
// app opens on Day. Notes points at the journal until Phase 4 builds the Notes | Journal switch.
//
// A "PUSH" IS DEPTH WITHIN ONE TAB. Board 25 has exactly three movements: a tab switch (fade
// through), a push (a detail slides in from the right) and a back (the reverse). Which one a
// navigation is depends only on where it starts and ends, so it is decided here, once:
//   · different tab key            → tab switch
//   · same tab, deeper             → push
//   · same tab, shallower          → back
//   · same tab, same depth         → no transition (a thread to another thread, `?date=` moving)
// ============================================================================

export type TabKey = 'day' | 'school' | 'chat' | 'reminders' | 'notes'

export interface TabDef {
  key: TabKey
  label: string
  /** The tab's root route — where a tap on the tab goes. */
  to: string
}

export const TABS: readonly TabDef[] = [
  { key: 'day', label: 'Day', to: '/' },
  { key: 'school', label: 'School', to: '/school' },
  { key: 'chat', label: 'Chat', to: '/chat' },
  // The route keeps its old name: `/tasks` is what notifications, the desktop and the API link to.
  { key: 'reminders', label: 'Reminders', to: '/tasks' },
  // Interim (Phase 4 adds Notes | Journal): Notes opens the journal's month list.
  { key: 'notes', label: 'Notes', to: '/journal' },
]

function under(pathname: string, root: string): boolean {
  return pathname === root || pathname.startsWith(`${root}/`)
}

/**
 * Which TAB a path belongs to. Everything not claimed by a tab belongs to Day, which is the
 * home: `/today` and `/day` (the prototype), `/calendar`, the one settings screen, and the
 * routes that render DesktopOnly.
 *
 * Recordings belong to School: `/school/recordings` lists them, `/recordings/:id` is one of them,
 * and `/record` is where the "tap to record" notification and the share sheet land.
 */
export function tabKey(pathname: string): TabKey {
  if (under(pathname, '/school') || under(pathname, '/recordings') || under(pathname, '/record')) {
    return 'school'
  }
  if (under(pathname, '/tasks')) return 'reminders'
  if (under(pathname, '/chat')) return 'chat'
  if (under(pathname, '/journal')) return 'notes'
  return 'day'
}

export function tabIndex(key: TabKey): number {
  return TABS.findIndex((t) => t.key === key)
}

export function tabRoot(key: TabKey): string {
  return TABS[tabIndex(key)]!.to
}

const ROOTS = new Set(['/', '/today', '/school', '/chat', '/tasks', '/journal'])

/**
 * How deep a path is inside its tab. 0 = the tab's own screen; 1 = a detail reached from it;
 * 2 = a detail reached from a detail (a recording from the Recordings list, a lecture's study
 * screen from its course).
 */
export function routeDepth(pathname: string): number {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (ROOTS.has(path)) return 0
  if (under(path, '/recordings') || path === '/record') return 2
  if (/^\/school\/[^/]+\/lecture\//.test(path)) return 2
  return 1
}

/** Same tab, deeper: the detail slides in from the right. */
export function isPush(from: string, to: string): boolean {
  return tabKey(from) === tabKey(to) && routeDepth(to) > routeDepth(from)
}

/** Same tab, shallower: the reverse. */
export function isBack(from: string, to: string): boolean {
  return tabKey(from) === tabKey(to) && routeDepth(to) < routeDepth(from)
}

export type TransitionKind = 'tab' | 'push' | 'back' | 'none'

/**
 * What a navigation looks like. A REPLACE is never animated: it is the app correcting its own
 * address (a new chat thread getting its id, an unknown route falling back to `/`), not the owner
 * going somewhere, and sliding a screen in over itself would be the app inventing a movement.
 */
export function transitionKind(
  from: string,
  to: string,
  navigationType: 'PUSH' | 'POP' | 'REPLACE' = 'PUSH',
): TransitionKind {
  if (from === to || navigationType === 'REPLACE') return 'none'
  if (tabKey(from) !== tabKey(to)) return 'tab'
  if (isPush(from, to)) return 'push'
  if (isBack(from, to)) return 'back'
  return 'none'
}

// ── Back from a detail screen ───────────────────────────────────────────────────────────────

/**
 * Where a detail screen's back control goes. With the bar hidden on a pushed screen, back is the
 * only way out, so it must work on a COLD OPEN too: the class-time notification opens
 * `/record?course=…` with nothing behind it, and `navigate(-1)` there would leave the app (or do
 * nothing).
 *
 * The previous entry is "ours" when the router itself put one behind this page: React Router's
 * BrowserRouter stamps an `idx` on every history entry it creates, counting from 0 at the entry
 * the app was opened on. So: one history entry, or `idx` 0 / missing → the tab's own screen,
 * replacing this one; otherwise → one step back.
 */
export type BackTarget = { kind: 'history' } | { kind: 'root'; to: string }

export function backTarget(
  pathname: string,
  history: { length: number; idx: number | null | undefined },
): BackTarget {
  const ours = history.length > 1 && typeof history.idx === 'number' && history.idx > 0
  return ours ? { kind: 'history' } : { kind: 'root', to: tabRoot(tabKey(pathname)) }
}

/**
 * The marker a back-by-replace carries in its location state, so the nav layer animates it as a
 * back (a plain REPLACE is never animated — see `transitionKind`).
 */
export const BACK_STATE = { mobileBack: true } as const

export function isBackState(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { mobileBack?: unknown }).mobileBack === true
}

// ── Edge-swipe back (board 25: "an edge-swipe from the left drags it live") ──────────────────

/** A press this close to the left edge of a pushed screen can start a back swipe. */
export const BACK_EDGE_PX = 24
/** Direction is decided after this much movement — before the browser's own scroll slop. */
export const BACK_DECIDE_PX = 8
/** Release past this share of the width completes the back... */
export const BACK_COMMIT_FRACTION = 0.35
/** ...or faster than this, in px per ms, whatever the distance. */
export const BACK_FLING_PX_PER_MS = 0.5
/** How far the page underneath sits to the left while a detail covers it. */
export const UNDER_DRIFT_PERCENT = -28
/** How dark the page underneath is while covered. */
export const UNDER_BRIGHTNESS = 0.82

export type SwipeAxis = 'pending' | 'back' | 'cancel'

/**
 * After the finger has moved `dx`,`dy` from where it went down: still deciding, a back swipe, or
 * not ours. Vertical wins ties so a scroll that wobbles is never stolen, and a leftward drag is
 * never a back.
 */
export function decideSwipeAxis(dx: number, dy: number, slop: number = BACK_DECIDE_PX): SwipeAxis {
  if (Math.hypot(dx, dy) < slop) return 'pending'
  return dx > 0 && Math.abs(dx) > Math.abs(dy) ? 'back' : 'cancel'
}

/** 0 at rest, 1 when the detail has left the screen. 1:1 with the finger, clamped. */
export function backProgress(dx: number, width: number): number {
  if (width <= 0) return 0
  return Math.min(1, Math.max(0, dx / width))
}

/** Release: complete the back past 35% of the width, or on a rightward fling over 0.5 px/ms. */
export function shouldCompleteBack(dx: number, width: number, velocity: number): boolean {
  if (width <= 0) return false
  return dx > width * BACK_COMMIT_FRACTION || velocity > BACK_FLING_PX_PER_MS
}

/** The page underneath at progress `p`: un-drifts and un-darkens in proportion. */
export function underAt(p: number): { translatePercent: number; brightness: number } {
  return {
    translatePercent: UNDER_DRIFT_PERCENT * (1 - p),
    brightness: UNDER_BRIGHTNESS + (1 - UNDER_BRIGHTNESS) * p,
  }
}

/**
 * Velocity from the last few samples, px per ms. Only the trailing `windowMs` counts, so a drag
 * that paused before release is not a fling.
 */
export function velocityOf(samples: readonly { x: number; t: number }[], windowMs = 80): number {
  if (samples.length < 2) return 0
  const last = samples[samples.length - 1]!
  let first = last
  for (let i = samples.length - 2; i >= 0; i--) {
    const s = samples[i]!
    if (last.t - s.t > windowMs) break
    first = s
  }
  const dt = last.t - first.t
  return dt > 0 ? (last.x - first.x) / dt : 0
}

// ── The stage: which pages are mounted, and in what role ─────────────────────────────────────

/** The part of a router location the stage cares about. */
export interface Loc {
  pathname: string
  search: string
  hash: string
}

export function locKey(loc: Loc): string {
  return `${loc.pathname}${loc.search}${loc.hash}`
}

/** One mounted page. `id` is its React key: a page keeps its id — and so its state and scroll — while it changes role. */
export interface Entry<L extends Loc = Loc> {
  id: number
  location: L
  /** Shown as a pushed detail: slid in over a page, tab bar away. Decided when the entry is made. */
  pushed: boolean
  /** The page underneath IS the previous history entry, so "back" is `navigate(-1)`. */
  fromHistory: boolean
  /**
   * Is it rendered? Always, except a tab root put under a deep-linked detail (a notification
   * opening `/record`, a link straight to `/calendar`): that one is mounted only when an edge-swipe
   * starts to uncover it, so opening a detail never also renders a whole second screen behind it.
   */
  live: boolean
}

export type StageAnim = 'tab' | 'push' | 'back'

export interface Stage<L extends Loc = Loc> {
  current: Entry<L>
  /** Mounted beneath a pushed `current`: what the edge-swipe uncovers. */
  under: Entry<L> | null
  /** On its way out: the old tab fading, or the detail sliding away. */
  leaving: Entry<L> | null
  anim: StageAnim | null
}

export function rootLoc(key: TabKey): Loc {
  return { pathname: tabRoot(key), search: '', hash: '' }
}

/** The first stage: a deep link into a detail mounts its tab's root underneath it. */
export function initialStage<L extends Loc>(loc: L, newId: () => number, asLoc: (l: Loc) => L): Stage<L> {
  const pushed = routeDepth(loc.pathname) > 0
  return {
    current: { id: newId(), location: loc, pushed, fromHistory: false, live: true },
    under: pushed
      ? {
          id: newId(),
          location: asLoc(rootLoc(tabKey(loc.pathname))),
          pushed: false,
          fromHistory: false,
          live: false,
        }
      : null,
    leaving: null,
    anim: null,
  }
}

/**
 * THE NAV LAYER'S ONE RULE: given the stage and where the router just went, what is mounted next.
 *
 * `swiped` — the edge-swipe already animated this back, so the page underneath simply becomes the
 * page, with no second animation. `reduced` — reduced motion: a tab switch does not keep the old
 * page around to fade (the new one fades in alone).
 */
export function nextStage<L extends Loc>(
  prev: Stage<L>,
  loc: L,
  navigationType: 'PUSH' | 'POP' | 'REPLACE',
  opts: { swiped: boolean; reduced: boolean; newId: () => number; asLoc: (l: Loc) => L },
): Stage<L> {
  const { swiped, reduced, newId, asLoc } = opts
  const from = prev.current.location
  const rootUnder = (pathname: string): Entry<L> => ({
    id: newId(),
    location: asLoc(rootLoc(tabKey(pathname))),
    pushed: false,
    fromHistory: false,
    live: false,
  })

  if (locKey(from) === locKey(loc)) {
    return { ...prev, current: { ...prev.current, location: loc } }
  }

  if (swiped && prev.under) {
    const current = { ...prev.under, location: loc, live: true }
    return {
      current,
      under: current.pushed ? rootUnder(loc.pathname) : null,
      leaving: null,
      anim: null,
    }
  }

  const kind = transitionKind(from.pathname, loc.pathname, navigationType)

  if (kind === 'tab') {
    const pushed = routeDepth(loc.pathname) > 0
    return {
      current: { id: newId(), location: loc, pushed, fromHistory: false, live: true },
      under: pushed ? rootUnder(loc.pathname) : null,
      leaving: reduced ? null : prev.current,
      anim: 'tab',
    }
  }

  if (kind === 'push') {
    return {
      current: { id: newId(), location: loc, pushed: true, fromHistory: false, live: true },
      under: { ...prev.current, fromHistory: true },
      leaving: null,
      anim: 'push',
    }
  }

  if (kind === 'back' && prev.current.pushed) {
    const promoted =
      prev.under && locKey(prev.under.location) === locKey(loc)
        ? { ...prev.under, location: loc, live: true }
        : { id: newId(), location: loc, pushed: routeDepth(loc.pathname) > 0, fromHistory: false, live: true }
    return {
      current: promoted,
      under: promoted.pushed ? rootUnder(loc.pathname) : null,
      leaving: prev.current,
      anim: 'back',
    }
  }

  // Same tab, same depth (or a REPLACE): the page stays mounted and follows the address.
  return { ...prev, current: { ...prev.current, location: loc } }
}

export type LayerRole = 'current' | 'under' | 'leaving'

/**
 * The animation one layer plays (a `data-anim` value; the keyframes are in index.css). Reduced
 * motion turns every movement into a 160ms opacity fade: an arriving page fades in, a leaving one
 * fades out, and the page underneath does not move.
 */
export function layerAnim(role: LayerRole, anim: StageAnim | null, reduced: boolean): string | null {
  if (anim === null) return null
  const full: Record<StageAnim, Partial<Record<LayerRole, string>>> = {
    tab: { current: 'tab-in', leaving: 'tab-out' },
    push: { current: 'push-in', under: 'under-in' },
    back: { current: 'under-out', leaving: 'push-out' },
  }
  const name = full[anim][role] ?? null
  if (!reduced || name === null) return name
  // The page being covered stays exactly where it is, visible, while the detail fades in over it.
  if (role === 'under') return 'under-hold'
  if (role === 'leaving') return 'fade-out'
  // A page coming back from underneath is already there; only something new fades in.
  return anim === 'back' ? null : 'fade-in'
}
